import type { Experiment } from 'types';
import type { SampleRatioResult } from 'helpers/sample-ratio';
import type { useExperimentStats } from '@/state/experiments/useExperimentStats';
import type { SegmentDimension } from '@/state/experiments/useExperimentSegments';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useExperimentSegments } from '@/state/experiments/useExperimentSegments';
import { ALL_CURRENCIES, revenueNeedsCurrency } from 'helpers/currency';
import { formatDate } from 'helpers/formatters';
import { ReportSection } from '../ReportSection';
import { SegmentTable } from './SegmentTable';
import { ReturnVisitsTable } from './ReturnVisitsTable';

type SegmentsProps = {
  experiment: Experiment;
  manager: ReturnType<typeof useExperimentStats>;
};

// Matches ChartTabs, so the two tab strips on the page read as one control style.
const TAB_CLASS =
  'micro-label data-[state=active]:bg-panel data-[state=active]:text-foreground rounded-lg px-3 py-1.5 ' +
  'text-nano transition-colors duration-150';

/** Below this share of conversions carrying an id, per-visitor conversion is mostly missing data. */
const LOW_EVENT_COVERAGE = 0.5;

const pct = (share: number): string => `${Math.round(share * 100)}%`;

type SplitBadgeProps = { split: SampleRatioResult | null };

const SplitBadge = ({ split }: SplitBadgeProps) => {
  if (split === null) return null;
  if (split.state === 'not-applicable') return <span className="supporting">too few to check the split</span>;
  if (split.state === 'ok') return <span className="supporting">split OK</span>;
  return <span className="micro-label text-warn">split does not match: read with care</span>;
};

/**
 * Per-visitor readings for websites with the visitor ID flag on, split by device and by
 * new vs returning, plus return visits. Last on the page: it is detail, and it never
 * feeds the verdict above it. Renders nothing when the experiment has no visitor-id data.
 */
export const Segments = ({ experiment, manager }: SegmentsProps) => {
  const segments = useExperimentSegments({ experiment, currency: manager.currency });
  const { data, coverage } = segments;
  if (!data || !coverage) return null;

  const currency = manager.currency === ALL_CURRENCIES ? null : manager.currency;
  const showRevenue = !revenueNeedsCurrency('Revenue', manager.currency, manager.currencies);

  const mainRates = new Map(
    data.variants.map((v) => {
      const stats = manager.statistics?.[v.id];
      return [v.id, stats && stats.sessions > 0 ? stats.conversions / stats.sessions : null];
    }),
  );

  const idSince = data.idSince ? new Date(data.idSince) : null;
  const startedBeforeIds = idSince && experiment.startsAt ? experiment.startsAt < idSince.getTime() : false;
  const { lookbackDays, returnWindowDays, visitGapMinutes } = data.config;
  const historyFull = data.historySince
    ? new Date(new Date(data.historySince).getTime() + lookbackDays * 24 * 60 * 60 * 1000)
    : null;

  return (
    <ReportSection
      id="segments"
      title="Segments (exploratory)"
      summary={
        coverage.enrolmentShare === null ? undefined : `${pct(coverage.enrolmentShare)} of enrolments have a visitor id`
      }
    >
      <div className="flex flex-col gap-5">
        <div className="border-warn-line bg-warn-bg rounded-card flex gap-3 border px-4 py-3.5">
          <IconAlertTriangle className="text-warn mt-0.5 size-4 shrink-0" />
          <p className="text-foreground text-body leading-relaxed">
            Exploratory. Comparing many segments makes some differences look real by chance, so nothing here decides a
            winner. Only visitors with a visitor id are counted, and each is counted in the variant they were first
            assigned, so these numbers are per visitor and will not match the per-session results above.
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <p className="micro-label">Coverage</p>
          {idSince && (
            <p className="supporting">
              Visitor ids since {formatDate(idSince.getTime())}
              {startedBeforeIds &&
                experiment.startsAt &&
                `. The experiment started earlier, on ${formatDate(experiment.startsAt)}, and visitors who enrolled between then and ${formatDate(idSince.getTime())} have no id, so they are not included here`}
              .
            </p>
          )}
          {coverage.enrolmentShare !== null && (
            <p className="supporting">
              Enrolments with an id: {pct(coverage.enrolmentShare)} of those counted in the main results.
            </p>
          )}
          {coverage.eventShare !== null && (
            <p className={coverage.eventShare < LOW_EVENT_COVERAGE ? 'micro-label text-warn' : 'supporting'}>
              Conversions with an id: {data.coverage.events.withId.toLocaleString()} of{' '}
              {data.coverage.events.total.toLocaleString()} ({pct(coverage.eventShare)})
              {coverage.eventShare < LOW_EVENT_COVERAGE &&
                '. Many conversions arrive without an id, which usually means checkout happens on another domain, so conversion rates here read low'}
              .
            </p>
          )}
          {coverage.conflicted > 0 && (
            <p className="supporting">
              {coverage.conflicted.toLocaleString()} visitors seen in more than one variant are left out.
            </p>
          )}
        </div>

        <SegmentTable
          label="All visitors with an id"
          readings={segments.overall}
          currency={currency}
          showRevenue={showRevenue}
          mainRates={mainRates}
          heading={<h4 className="text-foreground text-body font-medium">All visitors with an id</h4>}
        />

        <div className="flex flex-col gap-3">
          <Tabs value={segments.dimension} onValueChange={(value) => segments.setDimension(value as SegmentDimension)}>
            <TabsList className="bg-field border-line h-auto gap-0.5 rounded-lg border p-0.5">
              <TabsTrigger value="device" className={TAB_CLASS}>
                Device
              </TabsTrigger>
              <TabsTrigger value="visitorType" className={TAB_CLASS}>
                New vs returning
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {segments.dimension === 'visitorType' && (
            <p className="supporting">
              Returning means a visit in the {lookbackDays} days before the one they enrolled in. Tabs opened within{' '}
              {visitGapMinutes} minutes of each other are one visit.
              {historyFull &&
                ` Session history starts ${formatDate(data.historySince ?? '')}, so visitors enrolled before ${formatDate(historyFull.getTime())} show as not enough history yet.`}{' '}
              Safari clears visitor ids after 7 days without a visit, so some returning Safari visitors count as new.
              These groups fill up at different times, so their split is not checked.
            </p>
          )}

          {segments.blocks.length === 0 ? (
            <p className="supporting">Nothing to split yet.</p>
          ) : (
            segments.blocks.map((block) => (
              <SegmentTable
                key={block.segment}
                label={block.label}
                readings={block.readings}
                currency={currency}
                showRevenue={showRevenue}
                heading={
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h4 className="text-foreground text-body font-medium">{block.label}</h4>
                    <SplitBadge split={block.split} />
                  </div>
                }
              />
            ))
          )}
          <p className="supporting">
            Dimmed rows have fewer than 100 visitors, too few for a lift to mean anything, so none is shown.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="micro-label">Return visits</p>
          <ReturnVisitsTable
            readings={segments.returnVisits}
            windowDays={returnWindowDays}
            visitGapMinutes={visitGapMinutes}
          />
        </div>
      </div>
    </ReportSection>
  );
};
